"use client";

import { AlertTriangle, X } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Presentational service-notice / apology banner.
 *
 * This is a PURE VIEW: it takes the already-resolved `payload` as a prop and
 * does NOT read PostHog. The flag + payload are evaluated SERVER-SIDE by the
 * `ServiceNoticeBanner` wrapper (see service-notice-banner.tsx), so the banner
 * renders for EVERY visitor regardless of analytics-consent state. (The old
 * client-only version rode `usePostHog()`, which never initialises for users
 * who declined analytics consent — so incident comms silently missed them.)
 *
 * The only client-side state here is the per-version dismissal, persisted to
 * localStorage and keyed by `payload.version` so bumping the version re-shows
 * the banner to users who dismissed a previous one.
 *
 * Expected payload shape (delivered from the PostHog flag's JSON payload):
 *
 * {
 *   "version": "2026-06-09-example",   ← bump to reset dismissed state
 *   "en": {
 *     "heading": "Heading text",
 *     "body": "Body text.",
 *     "linkText": "click here",         ← optional; omit to render no link
 *     "linkHref": "/en/login",          ← optional; defaults to /{locale}/login
 *     "dismissLabel": "Dismiss notice"  ← optional; falls back to "Dismiss"
 *   },
 *   "id": { ... }                       ← add any locale key as needed
 * }
 */

type LocaleContent = {
	heading: string;
	body: string;
	linkText?: string;
	linkHref?: string;
	dismissLabel?: string;
};

type BannerPayload = {
	version: string;
	[key: string]: LocaleContent | string;
};

function isBannerPayload(v: unknown): v is BannerPayload {
	return (
		typeof v === "object" &&
		v !== null &&
		typeof (v as Record<string, unknown>).version === "string" &&
		(v as Record<string, unknown>).version !== ""
	);
}

function isLocaleContent(v: unknown): v is LocaleContent {
	return (
		typeof v === "object" &&
		v !== null &&
		typeof (v as Record<string, unknown>).heading === "string" &&
		typeof (v as Record<string, unknown>).body === "string"
	);
}

export function ServiceNoticeBannerView({
	locale,
	payload,
}: {
	locale: string;
	payload: unknown;
}) {
	const parsed = isBannerPayload(payload) ? payload : null;
	const version = parsed?.version ?? null;

	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (!version) return;
		const key = `ld-service-notice-dismissed:${version}`;
		try {
			setDismissed(localStorage.getItem(key) === "1");
		} catch {
			// localStorage unavailable (SecurityError, private mode) — default to shown.
		}
	}, [version]);

	const storageKey = version ? `ld-service-notice-dismissed:${version}` : null;

	function dismiss() {
		if (!storageKey) return;
		try {
			localStorage.setItem(storageKey, "1");
		} catch {
			// best-effort dismiss persistence only — never block the click.
		}
		setDismissed(true);
	}

	if (!parsed || dismissed) return null;

	const localeData = parsed[locale];
	const enData = parsed.en;
	const content = isLocaleContent(localeData)
		? localeData
		: isLocaleContent(enData)
			? enData
			: null;

	if (!content) return null;

	const linkHref = content.linkHref ?? `/${locale}/login`;
	const dismissLabel = content.dismissLabel ?? "Dismiss";

	return (
		<div className="border-amber-500/40 border-b bg-amber-50 dark:border-amber-500/30 dark:bg-amber-950/40">
			<div className="mx-auto flex max-w-4xl items-start justify-between gap-3 px-4 py-2.5">
				<div className="flex min-w-0 items-start gap-2 text-amber-900 text-sm dark:text-amber-100">
					<AlertTriangle
						className="mt-0.5 h-4 w-4 shrink-0"
						aria-hidden="true"
					/>
					<span className="min-w-0">
						<span className="font-semibold">{content.heading}</span>{" "}
						{content.body}
						{content.linkText && (
							<>
								{" "}
								<a href={linkHref} className="underline">
									{content.linkText}
								</a>
								.
							</>
						)}
					</span>
				</div>
				<button
					type="button"
					onClick={dismiss}
					aria-label={dismissLabel}
					className="shrink-0 rounded-lg p-1 text-amber-900/70 transition-colors hover:bg-amber-500/10 hover:text-amber-900 dark:text-amber-100/70 dark:hover:text-amber-100"
				>
					<X className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}
